import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';

const FOCUSABLE_NODES_TRAPPED = [
  'a',
  'button',
  'input',
  'textarea',
  'select',
  '[tabindex]:not([tabindex="-1"])',
];

const cleanup = (instance) => {
  instance.element?.removeEventListener('keydown', instance.handleKeyDown);
};

class FocusTrapModifier extends Modifier {
  element;

  #nodesToTrap = FOCUSABLE_NODES_TRAPPED;
  #noTabIndexOnDisabled;

  handleKeyDown = (event) => {
    if (event.key === 'Tab') {
      // check every keydown as dom may have changed
      let focusableNodes = Array.from(this.element.querySelectorAll(this.#nodesToTrap.join(', ')));

      // allow customization when disabled button can be focused
      if (this.#noTabIndexOnDisabled) {
        focusableNodes = focusableNodes.filter((el) => !el.disabled);
      }

      // nothing to trap at this time
      if (focusableNodes.length === 0) {
        return;
      }

      // define the limits of what nodes are accessible
      const firstNode = focusableNodes[0];
      const lastNode = focusableNodes[focusableNodes.length - 1];

      // trap focus circularly within the element
      if (event.shiftKey && document.activeElement === firstNode) {
        event.preventDefault();
        lastNode.focus();
      } else if (!event.shiftKey && document.activeElement === lastNode) {
        event.preventDefault();
        firstNode.focus();
      }
    }
  };

  constructor() {
    super(...arguments);
    registerDestructor(this, cleanup);
  }

  modify(element, _, { customNodes = null, noTabIndexOnDisabled = true }) {
    this.element = element;
    this.#noTabIndexOnDisabled = noTabIndexOnDisabled;

    // allow customization of focus trap area
    if (Array.isArray(customNodes)) {
      this.#nodesToTrap = customNodes;
    }

    // give focus to the element to allow focus trap. the element must have a valid role attribute.
    this.element.setAttribute('tabindex', '-1');
    this.element.focus();

    // trap focus through keydown listener
    this.element.addEventListener('keydown', this.handleKeyDown);
  }
}

export default FocusTrapModifier;
